/*
 * Copyright (c) Nexign, JSC, 2020-2022.
 */
/**
 * Styled for Wizard
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.2.0
 */

import styled from 'styled-components';

import { icons } from '@assets/index';
import { TABLE_FOOTER_BOTTOM_STYLE } from '@components/Table/constants';
import { LoaderFrameStyled } from '@modules/loader/components/styled/LoaderStyled';
import { colors, fonts, globalThemeConstants, wizard } from '@theme/index';

export const WizardStyled = styled.div`
    position: relative;

    ${LoaderFrameStyled} {
        position: fixed;
    }
`;

export const WizardHeaderStyled = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding-top: 32px;
    border-top: 1px solid ${colors.wizardHeaderBorderBottomColor};
    padding-bottom: ${globalThemeConstants.gutter}px;
`;

interface IWizardHeaderItemStyled {
    isActive: boolean;
    isPast: boolean;
}

export const WizardHeaderItemStyled = styled.div.attrs(({ isActive, isPast }: IWizardHeaderItemStyled) => ({
    className: isPast ? 'is-past' : isActive ? 'active' : '',
}))<IWizardHeaderItemStyled>`
    margin-right: 24px;
    line-height: ${wizard.headerStepCircleSize};
    color: gray;

    i {
        background-color: ${colors.wizardHeaderStepCircleBgColor};
        border: solid 1px ${colors.wizardHeaderStepCircleBorderColor};
        border-color: ${colors.wizardHeaderStepCircleBorderColor};
        border-radius: 50%;
        display: inline-block;
        width: ${wizard.headerStepCircleSize};
        height: ${wizard.headerStepCircleSize};
        text-align: center;

        ${fonts.textMain};
        font-style: normal;
        color: ${colors.wizardHeaderStepCircleTextColor};

        margin-right: 4px;
    }

    &.is-past {
        color: ${colors.wizardHeaderStepCircleActiveTextColor};
        i {
            background-color: ${colors.wizardHeaderStepCircleActiveBorderColor};
            background-image: url(${icons.checkedIcon});
            background-position: center;
            background-repeat: no-repeat;
            border-color: ${colors.wizardHeaderStepCircleActiveBorderColor};
            color: transparent;
            user-select: none;
        }
    }

    &.active {
        color: ${colors.wizardHeaderStepCircleActiveTextColor};
        i {
            background-color: ${colors.wizardHeaderStepCircleActiveBgColor};
            border-color: ${colors.wizardHeaderStepCircleActiveBorderColor};
        }
    }
`;
export const WizardBodyStyled = styled.div`
    margin-bottom: 96px;
`;

export const WizardFooterStyled = styled.div.attrs({
    className: 'fixed-with-scrollbar',
})`
    position: fixed;
    z-index: 3;
    left: 0;
    bottom: ${TABLE_FOOTER_BOTTOM_STYLE}px;
    width: 100%;
    height: 64px;
    background: ${colors.tableFooterBgColor};
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.2);
`;

export const WizardControlsStyled = styled.div`
    width: ${globalThemeConstants.maxSiteWidth};

    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-self: center;
    align-items: center;
`;

export const WizardControlsGroupStyled = styled.div`
    display: flex;
    flex-direction: row;
    align-self: center;
    align-items: center;
`;

export const WizardControlsItemStyled = styled.div`
    display: flex;
    margin-right: 24px;

    &:first-child {
        margin-left: 36px;
    }
    &:last-child {
        margin-right: 36px;
    }
`;
